import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2, Crown, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CheckoutSuccess = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('polling'); // polling, success, failed, expired
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus('failed');
      return;
    }
    pollStatus(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const pollStatus = async (attempt) => {
    if (attempt >= 8) {
      setStatus('failed');
      return;
    }

    try {
      const res = await axios.get(`${API}/payment/checkout-status/${sessionId}`);
      const data = res.data;

      if (data.payment_status === 'paid') {
        setStatus('success');
        return;
      } else if (data.status === 'expired') {
        setStatus('expired');
        return;
      }

      // Continue polling
      setAttempts(attempt + 1);
      setTimeout(() => pollStatus(attempt + 1), 2500);
    } catch (err) {
      console.error('Status check error:', err);
      setAttempts(attempt + 1);
      setTimeout(() => pollStatus(attempt + 1), 3000);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Navbar />
      <div className="pt-24 pb-16 flex items-center justify-center min-h-[80vh]" data-testid="checkout-success-page">
        <div className="max-w-md w-full mx-4 text-center">
          {status === 'polling' && (
            <div>
              <Loader2 size={48} className="text-[#C5A059] animate-spin mx-auto mb-6" />
              <h1 className="text-2xl font-bold text-white mb-3">Processing Payment...</h1>
              <p className="text-gray-400">Verifying your payment. Please wait.</p>
              <div className="mt-4 flex justify-center gap-1">
                {[...Array(8)].map((_, i) => (
                  <div
                    key={i}
                    className={`w-2 h-2 rounded-full transition-colors ${
                      i <= attempts ? 'bg-[#C5A059]' : 'bg-white/10'
                    }`}
                  />
                ))}
              </div>
            </div>
          )}

          {status === 'success' && (
            <div>
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-green-500/20 mb-6">
                <CheckCircle size={40} className="text-green-400" />
              </div>
              <h1 className="text-3xl font-bold text-white mb-3">Welcome to Pro!</h1>
              <p className="text-gray-400 mb-2">Your payment was successful.</p>
              <div className="inline-flex items-center gap-2 bg-[#C5A059]/10 border border-[#C5A059]/30 rounded-full px-4 py-2 mb-8">
                <Crown size={16} className="text-[#C5A059]" />
                <span className="text-[#C5A059] font-semibold text-sm">DigitalGoldGang Pro Active</span>
              </div>
              <div className="space-y-3">
                <Link to="/ai-advisor">
                  <Button className="w-full bg-gradient-to-r from-[#C5A059] to-[#D4AF37] text-black font-bold py-5" data-testid="go-to-advisor-btn">
                    Open AI Advisor <ArrowRight size={16} className="ml-2" />
                  </Button>
                </Link>
                <Link to="/macro-brief">
                  <Button variant="outline" className="w-full border-white/20 text-gray-300 py-5 mt-3" data-testid="go-to-briefs-btn">
                    View Macro Briefs
                  </Button>
                </Link>
              </div>
            </div>
          )}

          {status === 'failed' && (
            <div>
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-red-500/20 mb-6">
                <XCircle size={40} className="text-red-400" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-3">Payment Verification Failed</h1>
              <p className="text-gray-400 mb-6">We couldn't verify your payment. If you were charged, your Pro access will be activated shortly.</p>
              <Link to="/">
                <Button variant="outline" className="border-white/20 text-gray-300">
                  Return Home
                </Button>
              </Link>
            </div>
          )}

          {status === 'expired' && (
            <div>
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-yellow-500/20 mb-6">
                <XCircle size={40} className="text-yellow-400" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-3">Session Expired</h1>
              <p className="text-gray-400 mb-6">Your checkout session has expired. Please try again.</p>
              <Link to="/ai-advisor">
                <Button className="bg-[#C5A059] text-black hover:bg-[#D4AF37]">
                  Try Again
                </Button>
              </Link>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default CheckoutSuccess;
